import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, TextField, Button, MenuItem, Alert, useTheme, useMediaQuery, Paper, Stack, alpha } from '@mui/material';
import { addUserLocation, updateUserLocation, getUserLocations } from './userLocationsApi';
import { fetchCities } from './api';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

function LocationMarker({ position, setPosition }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.flyTo(position, map.getZoom());
    }
  }, [position, map]);

  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  return position === null ? null : (
    <Marker position={position} draggable={true} eventHandlers={{
      dragend: (e) => {
        setPosition(e.target.getLatLng());
      }
    }}>
    </Marker>
  );
}

function AddEditLocation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isEdit = !!id;

  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Map state
  const [position, setPosition] = useState({ lat: 15.3694, lng: 44.1910 }); // Default Sanaa
  const [mapZoom] = useState(13);

  useEffect(() => {
    fetchCities().then(setCities).catch(() => setCities([]));
    
    if (isEdit) {
      loadLocation();
    }
  }, [id, isEdit]);

  const loadLocation = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const locations = await getUserLocations(user.Id || user.id);
        const loc = locations.find(l => l.Id === Number(id));
        if (loc) {
          setName(loc.Name);
          setCity(loc.City);
          setAddress(loc.Address);
          if (loc.Latitude && loc.Longitude) {
             setPosition({ lat: loc.Latitude, lng: loc.Longitude });
          }
        } else {
          setError(t('locations.notFound', 'Location not found'));
        }
      }
    } catch (err) {
      console.error(err);
      setError(t('common.error', 'An error occurred'));
    }
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setPosition({ lat: latitude, lng: longitude });
        },
        (err) => {
          console.error("Error getting location", err);
          setError(t('locations.locationError', 'Could not get your location'));
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setError(t('locations.notSupported', 'Geolocation is not supported'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) throw new Error('User not logged in');
      const user = JSON.parse(userStr);

      const locationData = {
        UserId: user.Id || user.id,
        Name: name,
        City: city,
        Address: address,
        Latitude: position.lat,
        Longitude: position.lng,
        IsDefault: false 
      };

      if (isEdit) {
        await updateUserLocation(id, { ...locationData, Id: Number(id) });
      } else {
        await addUserLocation(locationData);
      }

      navigate('/locations');
    } catch (err) {
      console.error(err);
      setError(t('common.error', 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        mt: { xs: 2, sm: 3, md: 4 }, 
        mb: { xs: 8, sm: 10 }, 
        px: { xs: 2, sm: 3 },
        direction: i18n.dir() 
      }}
    >
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: { xs: 2, sm: 3 },
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <LocationOnIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
            }}
          >
            {isEdit ? t('locations.editTitle', 'Edit Address') : t('locations.addTitle', 'Add New Address')}
          </Typography>
        </Stack>
      </Paper>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            fontSize: { xs: '0.85rem', sm: '0.95rem' }
          }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <Box 
        component="form" 
        onSubmit={handleSubmit} 
        sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}
      >
        
        {/* Map Section */}
        <Paper 
          elevation={3}
          sx={{ 
            height: { xs: '300px', sm: '350px', md: '400px' }, 
            width: '100%', 
            position: 'relative',
            borderRadius: 3,
            overflow: 'hidden',
            border: `2px solid ${theme.palette.divider}`
          }}
        >
          <MapContainer 
            center={position} 
            zoom={mapZoom} 
            style={{ height: '100%', width: '100%' }} 
            attributionControl={false}
            zoomControl={!isMobile}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LocationMarker position={position} setPosition={setPosition} />
          </MapContainer>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<MyLocationIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            onClick={handleGetCurrentLocation}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              position: 'absolute', 
              top: { xs: 8, sm: 10 }, 
              right: { xs: 8, sm: 10 }, 
              zIndex: 1000,
              boxShadow: 3,
              fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
              py: { xs: 0.8, sm: 1 },
              px: { xs: 1.5, sm: 2 },
              '&:active': { transform: isMobile ? 'scale(0.95)' : 'none' }
            }}
          >
            {isMobile ? t('locations.myLocation', 'My Location') : t('locations.useCurrentLocation', 'Use Current Location')}
          </Button>
          
          {/* Map Instruction */}
          <Paper
            elevation={2}
            sx={{
              position: 'absolute',
              bottom: { xs: 8, sm: 10 },
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: alpha(theme.palette.common.white, 0.95),
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.8, sm: 1 },
              borderRadius: 2,
              zIndex: 1000
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                color: 'text.secondary'
              }}
            >
              {t('locations.tapToSelect', '📍 Tap or drag marker to select location')}
            </Typography>
          </Paper>
        </Paper>

        {/* Form Fields */}
        <TextField
          label={t('locations.nameLabel', 'Location Name (e.g. Home, Work)')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          size={isMobile ? "small" : "medium"}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.9rem', sm: '1rem' }
            },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }
          }}
        />
        
        <TextField
          select
          label={t('userProfile.city', 'City')}
          value={city}
          onChange={(e) => setCity(e.target.value)}
          required
          fullWidth
          size={isMobile ? "small" : "medium"}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.9rem', sm: '1rem' }
            },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }
          }}
        >
          {cities.map((c) => (
            <MenuItem 
              key={c.Id || c} 
              value={c.Name || c}
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              {c.Name || c}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label={t('userProfile.address', 'Address Details')}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          required
          fullWidth
          multiline
          rows={isMobile ? 2 : 3}
          size={isMobile ? "small" : "medium"}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.9rem', sm: '1rem' }
            },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }
          }}
        />

        {/* Action Buttons */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={{ xs: 1.5, sm: 2 }} 
          sx={{ mt: { xs: 1, sm: 2 } }}
        >
          <Button 
            variant="contained" 
            type="submit" 
            disabled={loading}
            fullWidth
            startIcon={<SaveIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            size={isMobile ? "large" : "large"}
            sx={{ 
              bgcolor: 'primary.main', 
              py: { xs: 1.2, sm: 1.5 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 700,
              '&:hover': { bgcolor: 'primary.dark' },
              '&:active': { transform: isMobile ? 'scale(0.98)' : 'none' }
            }}
          >
            {loading ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/locations')}
            fullWidth
            startIcon={<CancelIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            size={isMobile ? "large" : "large"}
            sx={{ 
              py: { xs: 1.2, sm: 1.5 }, 
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 700,
              color: 'primary.main', 
              borderColor: 'primary.main',
              '&:active': { transform: isMobile ? 'scale(0.98)' : 'none' }
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
}

export default AddEditLocation;
